import * as React from "react";
import { formSetValue, useCpFormValue } from "../../../gvc/testNewForm/utils";
import { CpFormItem } from "../../../gvc/testNewForm/CpFormItem";
import { CpInput } from "../../../gvc/testNewForm/CpInput";
import { CpLabel } from "../../../gvc/testNewForm/CpLabel";
import { CpRadioCardGroup } from "../../../gvc/testNewForm/CpRadioCardGroup";
import { ImageIcon } from "../../../../components/Icons/svgComponents/ImageIcon";
import { CpErrorWarning } from "../../../gvc/testNewForm/CpErrorWarning";
import { useCpFormContext } from "../../../../components/form/useCpFormContext";
import { CpKindSelect } from "../../../gvc/testNewForm/temp/CpKindSelect";
import { CpLabelTooltip } from "../../../gvc/testNewForm/CpLabelTooltip";
import { CpTooltip } from "../../../gvc/testNewForm/CpTooltip";
import { CpLabelDirtyIndicator } from "../../../gvc/testNewForm/CpLabelDirtyIndicator";
import { Only } from "../only";

interface Props {
  index: number;
}
export const CpInputContainerImage: React.FC<Props> = ({ index }) => {
  const { id } = useCpFormContext();

  const name = useCpFormValue(`containers.${index}.name`);
  const image = useCpFormValue(`containers.${index}.image`);

  // Reset cplnFetchAttempted and isFromSavedWorkload when cplnValue or isExternal changes
  const prevCplnLink = React.useRef(image.cplnValue?.link);
  const prevIsExternal = React.useRef(image.isExternal);
  React.useLayoutEffect(() => {
    const linkChanged = prevCplnLink.current !== image.cplnValue?.link;
    const isExternalChanged = prevIsExternal.current !== image.isExternal;

    if (linkChanged || isExternalChanged) {
      formSetValue(id, `containers.${index}.image.cplnFetchAttempted`, false);
      formSetValue(id, `containers.${index}.image.isFromSavedWorkload`, false);
      formSetValue(id, `containers.${index}.image.cplnValue.item`, undefined);
      prevCplnLink.current = image.cplnValue?.link;
      prevIsExternal.current = image.isExternal;
    }
  }, [image.cplnValue?.link, image.isExternal]);

  // automatic name from container image
  React.useEffect(() => {
    if (name.isManual) {
      return;
    }
    const imageValue = image.isExternal ? image.externalValue : image.cplnValue.link;
    if (!imageValue) {
      return;
    }

    let calculatedName = imageValue.split("/").pop()!.split("@").shift()!.split(":").shift()!.toLowerCase();
    if (calculatedName.startsWith("cpln-")) {
      calculatedName = calculatedName.replace("cpln-", "");
    }

    if (name.value !== calculatedName) {
      formSetValue(id, `containers.${index}.name.value`, calculatedName);
    }
  }, [name, image]);

  return (
    <>
      <CpFormItem noMargin>
        <CpLabelTooltip>
          <CpLabel name={"skip"}>
            Image
            <Only name={`meta.isCreate`} checkFn={(v) => v === false}>
              <CpLabelDirtyIndicator name={`containers.${index}.image`} />
            </Only>
          </CpLabel>
          <CpTooltip
            tooltipProps={{
              title: (
                <>
                  <div>The full image and tag path for a public or private docker registry.</div>
                  <div>
                    Private container images must have valid credentials configured in 'pullSecretLinks' on the GVC
                    (Global Virtual Cloud).
                  </div>
                </>
              ),
            }}
          />
        </CpLabelTooltip>
        <CpRadioCardGroup
          name={`containers.${index}.image.isExternal`}
          options={[
            {
              label: "Control Plane",
              value: false,
              boldLabel: true,
              children: (
                <div className="flex items-center gap-2 text-sm">
                  <img
                    className="object-contain"
                    style={{ width: 16, height: 16 }}
                    src={`/resources/logos/controlPlaneLogoOnly.svg`}
                  />
                  <span>Use an image from your CPLN repository</span>
                </div>
              ),
            },
            {
              label: "External",
              value: true,
              boldLabel: true,
              children: (
                <div className="flex items-center gap-2 text-sm">
                  <ImageIcon className="container-image-icon" style={{ width: 16, height: 16 }} />
                  <span>Use an image from an external registry</span>
                </div>
              ),
            },
          ]}
        />
        {image.isExternal ? (
          <CpInput
            name={`containers.${index}.image.externalValue`}
            placeholder="Type an Image"
            style={{ maxWidth: 600 }}
          />
        ) : (
          <CpKindSelect
            name={`containers.${index}.image.cplnValue`}
            kind="image"
            placeholder="Select an Image"
            optionValue="name"
            style={{ maxWidth: 600 }}
          />
        )}
        <CpErrorWarning name={`containers.${index}.imageroot`} />
      </CpFormItem>
    </>
  );
};
